import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { useLatest } from "react-use";
import { Flex } from "src/components/Flex.tsx";
import { EdgeShadow } from "src/components/Tabs/EdgeShadow.tsx";
import { Tab } from "src/components/Tabs/Tab.tsx";
import { TabsScroll } from "src/components/Tabs/TabsScroll.tsx";
import { PlusButton } from "src/components/Tabs/PlusButton.tsx";
import { TabsDropdown } from "src/components/Tabs/TabsDropdown.tsx";
import { TabsDropdownTrigger } from "src/components/Tabs/TabsDropdownTrigger.tsx";

import { css } from "@emotion/react";
import { useTabsStore } from "src/components/Tabs/TabsProvider.tsx";
import type { Tab as TabModel } from "src/lib/tabs-ui/TabsStore.tsx";
import { observer, Observer } from "mobx-react-lite";
import { TooltipV2 } from "src/components/TooltipV2/TooltipV2.tsx";

type ListProps = {
  onPlusButtonClick?: () => void;
  plusButtonHelperText: ReactNode;
  hidePlusButton?: boolean;
  disablePlusButton?: boolean;
};
export const TabsList = observer(function TabsListObserved(props: ListProps) {
  const {
    hidePlusButton,
    disablePlusButton,
    onPlusButtonClick = () => {},
    plusButtonHelperText,
  } = props;
  const store = useTabsStore();
  const activeTab = store.activeTab;

  const comebackActiveTabTimeout = useRef<NodeJS.Timeout | null>(null);

  const autoComebackActiveTabEnabled = useLatest(
    store.activeTabAutoComebackEnabled,
  ).current;

  const comebackActiveTab = () => {
    if (autoComebackActiveTabEnabled && activeTab) {
      activeTab.scrollIntoView();
    }
  };

  const handlePointerLeave = () => {
    comebackActiveTabTimeout.current = setTimeout(comebackActiveTab, 750);
  };

  const handlePointerEnter = () => {
    if (comebackActiveTabTimeout.current) {
      clearTimeout(comebackActiveTabTimeout.current);
    }
  };

  const setTabElRef = (tab: TabModel) => (node: HTMLButtonElement | null) => {
    return tab.setDomEl(node);
  };

  useEffect(() => {
    store.activeTab?.scrollIntoView();
  }, [store.activeTab]);

  return (
    <div
      css={listStyles}
      onPointerLeave={handlePointerLeave}
      onPointerEnter={handlePointerEnter}
    >
      <Flex css={listInnerStyles}>
        <Observer>
          {() => (
            <>
              {store.pinnedTabs.map((tab) => (
                <Tab
                  showPlusButton={!hidePlusButton}
                  onPointerEnter={() => store.setHoveredTabId(tab.id)}
                  onPointerLeave={() => store.setHoveredTabId(undefined)}
                  key={tab.id}
                  tab={tab}
                />
              ))}
            </>
          )}
        </Observer>
      </Flex>
      <EdgeShadow enabled={() => !store.scrollAreaStartReached} side="right" />
      <div css={tabsStyles}>
        <TabsScroll
          onStartReached={(reached) => store.setScrollAreaStartReached(reached)}
          onEndReached={(reached) => store.setScrollAreaEndReached(reached)}
          ref={(node) => store.setContainerEl(node)}
        >
          <Observer>
            {() => (
              <>
                {store.unpinnedTabs.map((tab) => (
                  <Tab
                    showPlusButton={!hidePlusButton}
                    ref={setTabElRef(tab)}
                    onPointerEnter={() => store.setHoveredTabId(tab.id)}
                    onPointerLeave={() => store.setHoveredTabId(undefined)}
                    key={tab.id}
                    tab={tab}
                  />
                ))}
              </>
            )}
          </Observer>
        </TabsScroll>
      </div>
      <EdgeShadow enabled={() => !store.scrollAreaEndReached} side="left" />
      {!hidePlusButton && (
        <Flex css={addNewButtonWrapperStyles}>
          <TooltipV2
            disabled={disablePlusButton}
            delay={750}
            content={plusButtonHelperText}
          >
            <PlusButton
              disabled={disablePlusButton}
              onClick={onPlusButtonClick}
            />
          </TooltipV2>
        </Flex>
      )}

      <TabsDropdown>
        <Observer>
          {() => (
            <TabsDropdownTrigger
              style={{
                visibility:
                  store.outOfViewTabs.length > 0 ? "visible" : "hidden",
              }}
            />
          )}
        </Observer>
      </TabsDropdown>
    </div>
  );
});

const addNewButtonWrapperStyles = css({
  display: "flex",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
  marginLeft: "5px",
});

const listInnerStyles = css`
  flex-shrink: 0;
`;

const listStyles = css`
  display: flex;
  overflow: hidden;
  width: 100%;
  position: relative;
  z-index: 2;
  top: 1px;
  flex-shrink: 0;
`;

const tabsStyles = css`
  max-width: calc(100% - 62px);
  overflow: hidden;
`;
